import { create } from "zustand";
import type {
  PosEstadoDeLaFoto,
  PosEstadoDelAlmacen,
  PosParteDeLaFoto,
} from "@/platform/types";

/**
 * La foto de la caja (trd-client#345), del lado de la pantalla. La baja Rust
 * por partes; acá se guarda lo último que avisó, y el estado del almacén.
 */

export const FOTO_INICIAL: PosEstadoDeLaFoto = {
  businessId: null,
  partes: [],
  bajando: null,
  sinFoto: false,
  lista: false,
};

export interface FotoDeLaCaja {
  foto: PosEstadoDeLaFoto;
  almacen: PosEstadoDelAlmacen | null;
}

/** Vive fuera de React, como el latido: el mostrador se monta con cada ruta. */
export const useFotoDeLaCaja = create<FotoDeLaCaja>(() => ({
  foto: FOTO_INICIAL,
  almacen: null,
}));

const NOMBRE: Record<PosParteDeLaFoto, string> = {
  settings: "los ajustes",
  buyers: "los clientes",
  catalog: "el catálogo",
  prices: "los precios",
  stock: "el stock",
};

/**
 * La primera carga, en palabras del mostrador (#345 AC-7): qué parte baja y
 * cuánto falta. `null` cuando no hay nada que decir: la foto está lista, o
 * core todavía no la tiene y la caja sigue como en la ronda 1.
 */
export function progresoDeLaFoto(foto: PosEstadoDeLaFoto): string | null {
  if (foto.lista || foto.sinFoto) return null;
  const empezo =
    foto.bajando !== null ||
    foto.partes.some((p) => p.activa !== null || p.bajando !== null);
  if (!empezo) return null;

  const faltan = foto.partes.filter((p) => p.activa === null).length;
  const total = foto.partes.length;
  const actual = foto.partes.find((p) => p.parte === foto.bajando);
  const items = actual?.bajando?.items ?? 0;
  const bajando = foto.bajando
    ? `: bajando ${NOMBRE[foto.bajando]}${
        items > 0 ? ` (${items.toLocaleString("es-AR")} cargados)` : ""
      }`
    : "";
  return `Preparando la caja para vender sin conexión${bajando}. Faltan ${faltan} de ${total} partes; mientras tanto, se vende con conexión.`;
}

/**
 * Lo que tiene que hacer el dueño con la cola apartada (§2.2): la terminal
 * vieja sigue activa y sin señal hasta que la dé de baja, y la nueva la da de
 * alta alguien con «Depósitos».
 */
export const AVISO_COLA_APARTADA_PARA_EL_DUENO =
  "La cola de la caja de este equipo no se pudo leer con la clave del equipo y quedó apartada, sin borrarse: las ventas que no se habían subido están en ese archivo y quedan para soporte. Dá de baja la caja anterior, que si no sigue activa y sin señal, y alguien con permiso de Depósitos tiene que dar de alta el equipo de nuevo.";

export const AVISO_COLA_APARTADA =
  "La cola de esta caja no se pudo leer con la clave del equipo y quedó apartada, sin borrarse: sus ventas sin subir quedan para soporte. Avisale al dueño. Hasta que alguien con permiso de Depósitos dé de alta el equipo de nuevo, se vende con conexión.";
